import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Bot,
  CheckCircle2,
  Clock,
  Database,
  Eye,
  FileText,
  GitBranch,
  Layers3,
  ListChecks,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  Sparkles,
  StopCircle,
  UserCog,
  Waypoints,
  Workflow,
  FolderKanban,
  X,
  Zap,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import EditScheduleModal from '../configurations/ingestion/EditScheduleModal';
import LogsModal from './LogsModal';
import {
  fetchAvailableWorkflows,
  fetchWorkflowProjects,
  getAvailableWorkflowProjectUuid,
  workflowApiErrorMessage,
  type AvailableWorkflowDto,
  type WorkflowProjectDto,
} from '../workflowbuilder/postWorkflowService';
import './Workflows.css';
import WorkflowProjectsModal from './WorkflowProjectsModal';

const WORKFLOW_ICON_BY_NAME: Record<string, LucideIcon> = {
  Workflow,
  GitBranch,
  Waypoints,
  Zap,
  Layers3,
  Sparkles,
  Bot,
  Settings2,
  Database,
  FileText,
  FolderKanban,
};

function WorkflowRowIcon({ iconRef }: { iconRef: string | undefined }) {
  const name = (iconRef ?? 'Workflow').trim();
  const Icon = WORKFLOW_ICON_BY_NAME[name] ?? Workflow;
  return <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} aria-hidden />;
}

function ProjectSidebarIcon({ iconRef }: { iconRef: string | undefined }) {
  const name = (iconRef ?? 'FolderKanban').trim();
  const Icon = WORKFLOW_ICON_BY_NAME[name] ?? FolderKanban;
  return <Icon className="wf-project-sidebar__ico" strokeWidth={1.75} aria-hidden />;
}

function formatShortDate(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
  } catch {
    return '—';
  }
}

function isRunningStatus(status: string | undefined): boolean {
  return (status ?? '').trim().toLowerCase() === 'running';
}

const Workflows: React.FC = () => {
  const navigate = useNavigate();

  const [nameFilter, setNameFilter] = useState('');
  const [rows, setRows] = useState<AvailableWorkflowDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<{
    id: string;
    sourceName: string;
    scheduleType: 'Manual';
    isActive: boolean;
  } | null>(null);

  const [showLogsModal, setShowLogsModal] = useState(false);
  const [activeLogs, setActiveLogs] = useState<{ workflowId: string; name: string } | null>(null);
  const [showProjectsModal, setShowProjectsModal] = useState(false);

  const [projects, setProjects] = useState<WorkflowProjectDto[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  /** null = default to first project (when any); '__all__' = user chose All. */
  const [selectedProjectKey, setSelectedProjectKey] = useState<string | '__all__' | null>(null);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const list = await fetchWorkflowProjects();
      setProjects(list);
    } catch {
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  const loadList = useCallback(async () => {
    setListError(null);
    setLoading(true);
    try {
      const data = await fetchAvailableWorkflows(true);
      setRows(data);
    } catch (e) {
      setListError(workflowApiErrorMessage(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  const effectiveProjectKey = useMemo(() => {
    if (selectedProjectKey === '__all__') return '__all__';
    if (selectedProjectKey && projects.some((p) => p.uuid === selectedProjectKey)) return selectedProjectKey;
    if (projects.length > 0) return projects[0].uuid;
    return '__all__';
  }, [selectedProjectKey, projects]);

  const rowsForProject = useMemo(() => {
    if (effectiveProjectKey === '__all__') return rows;
    return rows.filter((w) => getAvailableWorkflowProjectUuid(w) === effectiveProjectKey);
  }, [rows, effectiveProjectKey]);

  const filtered = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return rowsForProject;
    return rowsForProject.filter(
      (w) =>
        w.name.toLowerCase().includes(q) || (w.description ?? '').toLowerCase().includes(q)
    );
  }, [rowsForProject, nameFilter]);

  const stats = useMemo(() => {
    let running = 0;
    let stopped = 0;
    let successRuns = 0;
    let errCount = 0;
    for (const w of rowsForProject) {
      if (isRunningStatus(w.status)) running += 1;
      else stopped += 1;
      successRuns += w.successful_runs ?? 0;
      errCount += w.errors_count ?? 0;
    }
    return { running, stopped, total: rowsForProject.length, successRuns, errCount };
  }, [rowsForProject]);

  const goToBuilder = (workflowId?: string) => {
    if (workflowId) {
      navigate(`/portal/flowbuilder/${encodeURIComponent(workflowId)}`);
    } else {
      navigate('/portal/flowbuilder');
    }
  };

  const openScheduleModal = (wf: AvailableWorkflowDto) => {
    setSelectedWorkflow({
      id: wf.id,
      sourceName: wf.name,
      scheduleType: 'Manual',
      isActive: true,
    });
    setShowScheduleModal(true);
  };

  const openLogs = (wf: AvailableWorkflowDto) => {
    setActiveLogs({ workflowId: wf.id, name: wf.name });
    setShowLogsModal(true);
  };

  const selectedProjectLabel =
    effectiveProjectKey === '__all__'
      ? 'All projects'
      : projects.find((p) => p.uuid === effectiveProjectKey)?.name ?? 'Project';

  return (
    <div className="wf-page">
      <header className="wf-page-header">
        <div className="wf-hero">
          <div className="wf-hero__mark" aria-hidden>
            <Waypoints className="wf-hero__mark-icon" strokeWidth={1.5} />
          </div>
          <div className="wf-hero__copy">
            <p className="wf-hero-eyebrow">Automation</p>
            <h2 className="wf-hero-title">Agent Orchestrations</h2>
            <p className="wf-hero-sub">
              Run and govern saved flows from one place: open the builder, review execution health, read logs, and
              manage schedules without switching context.
            </p>
          </div>
        </div>

        <div className="wf-kpi-grid" aria-label="Orchestration summary">
          <article className="wf-kpi wf-kpi--indigo">
            <div className="wf-kpi__icon" aria-hidden>
              <Layers3 className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div className="wf-kpi__body">
              <span className="wf-kpi__value">{stats.total}</span>
              <span className="wf-kpi__label">Total flows</span>
            </div>
          </article>
          <article className="wf-kpi wf-kpi--emerald">
            <div className="wf-kpi__icon" aria-hidden>
              <Activity className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div className="wf-kpi__body">
              <span className="wf-kpi__value">{stats.running}</span>
              <span className="wf-kpi__label">Running</span>
            </div>
          </article>
          <article className="wf-kpi wf-kpi--amber">
            <div className="wf-kpi__icon" aria-hidden>
              <StopCircle className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div className="wf-kpi__body">
              <span className="wf-kpi__value">{stats.stopped}</span>
              <span className="wf-kpi__label">Idle / stopped</span>
            </div>
          </article>
          <article className="wf-kpi wf-kpi--slate">
            <div className="wf-kpi__icon" aria-hidden>
              <CheckCircle2 className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div className="wf-kpi__body">
              <span className="wf-kpi__value">{stats.successRuns}</span>
              <span className="wf-kpi__label">Successful runs</span>
              {stats.errCount > 0 ? (
                <span className="wf-kpi__sub">
                  <AlertTriangle className="wf-kpi__sub-ico" strokeWidth={2} aria-hidden />
                  {stats.errCount} errors
                </span>
              ) : null}
            </div>
          </article>
        </div>

        <div className="wf-toolbar-surface">
          <div className="wf-search-wrap wf-search-wrap--grow">
            <Search className="wf-search-icon" aria-hidden />
            <input
              type="search"
              className="wf-search"
              placeholder="Filter by name or description…"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              aria-label="Filter orchestrations"
            />
            {nameFilter ? (
              <button
                type="button"
                className="wf-search-clear"
                onClick={() => setNameFilter('')}
                aria-label="Clear filter"
              >
                <X className="w-4 h-4" strokeWidth={2} aria-hidden />
              </button>
            ) : null}
          </div>
          <div className="wf-toolbar__actions">
            <button
              type="button"
              className="wf-ghost-cta"
              onClick={() => void loadList()}
              disabled={loading}
              title="Refresh list"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
              Refresh
            </button>
            <button
              type="button"
              className="wf-ghost-cta"
              onClick={() => setShowProjectsModal(true)}
              title="Create and manage workflow projects"
            >
              <FolderKanban className="w-4 h-4" aria-hidden />
              Manage projects
            </button>
            <button type="button" className="wf-primary-cta" onClick={() => goToBuilder()}>
              <Plus className="w-4 h-4" aria-hidden />
              New flow
            </button>
          </div>
        </div>
      </header>

      <div className="wf-layout-with-sidebar">
        <aside className="wf-project-sidebar" aria-label="Projects">
          <div className="wf-project-sidebar__head">
            <span className="wf-project-sidebar__title">Projects</span>
          </div>
          <nav className="wf-project-sidebar__nav">
            {loadingProjects ? (
              <div className="wf-project-sidebar__loading">Loading…</div>
            ) : (
              <>
                {projects.map((p) => {
                  const active = effectiveProjectKey === p.uuid;
                  return (
                    <button
                      key={p.uuid}
                      type="button"
                      className={`wf-project-sidebar__item${active ? ' wf-project-sidebar__item--active' : ''}`}
                      onClick={() => setSelectedProjectKey(p.uuid)}
                    >
                      <span className="wf-project-sidebar__item-icon">
                        <ProjectSidebarIcon iconRef={p.icon} />
                      </span>
                      <span className="wf-project-sidebar__item-label">{p.name}</span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  className={`wf-project-sidebar__item wf-project-sidebar__item--all${effectiveProjectKey === '__all__' ? ' wf-project-sidebar__item--active' : ''}`}
                  onClick={() => setSelectedProjectKey('__all__')}
                >
                  <span className="wf-project-sidebar__item-icon">
                    <Layers3 className="wf-project-sidebar__ico" strokeWidth={1.75} aria-hidden />
                  </span>
                  <span className="wf-project-sidebar__item-label">All</span>
                </button>
              </>
            )}
          </nav>
        </aside>

        <div className="wf-layout-main">
      {listError ? (
        <div className="wf-error-banner" role="alert">
          <span className="wf-error-banner__msg">
            <AlertCircle className="wf-error-banner__ico" aria-hidden />
            {listError}
          </span>
          <button type="button" className="wf-ghost-cta wf-ghost-cta--compact" onClick={() => void loadList()}>
            Try again
          </button>
        </div>
      ) : null}

      <section className="wf-panel" aria-labelledby="wf-panel-heading">
        <div className="wf-panel__bar">
          <div className="wf-panel__lead">
            <h3 id="wf-panel-heading" className="wf-panel__title">
              Flow registry
            </h3>
            <p className="wf-panel__hint">
              {effectiveProjectKey === '__all__'
                ? 'Post workflows · all projects'
                : `Post workflows · ${selectedProjectLabel}`}
            </p>
          </div>
          <p className="wf-panel__meta">
            {loading
              ? 'Loading…'
              : `Showing ${filtered.length} of ${rowsForProject.length}${effectiveProjectKey === '__all__' ? ` (${rows.length} total)` : ''}`}
          </p>
        </div>

        {loading ? (
          <div className="wf-skeleton-table" aria-busy="true" aria-label="Loading workflows">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="wf-skeleton-row" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="wf-empty wf-empty--in-panel">
            <Workflow className="w-12 h-12 mx-auto mb-3 opacity-40" aria-hidden />
            <p className="wf-empty__title">
              {rowsForProject.length === 0
                ? effectiveProjectKey === '__all__'
                  ? 'No workflows yet'
                  : 'No workflows in this project'
                : 'No matches'}
            </p>
            <p className="wf-empty__text">
              {rowsForProject.length === 0
                ? effectiveProjectKey === '__all__'
                  ? 'Create your first workflow in the builder, then it will appear here automatically.'
                  : 'Assign workflows to this project when saving from the flow builder, or pick another project.'
                : 'Try a different search term or choose All.'}
            </p>
            {rows.length === 0 && effectiveProjectKey === '__all__' ? (
            <button type="button" className="wf-primary-cta" onClick={() => goToBuilder()}>
              <Plus className="w-4 h-4" aria-hidden />
              Open flow builder
            </button>
            ) : null}
          </div>
        ) : (
          <div className="wf-table-scroll">
            <table className="wf-table">
              <caption className="wf-table__caption">Saved post-workflow flows and run summary</caption>
              <thead>
                <tr>
                  <th scope="col" className="wf-table__th wf-table__th--workflow">
                    Workflow
                  </th>
                  <th scope="col" className="wf-table__th wf-col--desc">
                    Description
                  </th>
                  <th scope="col" className="wf-table__th wf-table__th--narrow">
                    Status
                  </th>
                  <th scope="col" className="wf-table__th wf-table__th--num">
                    Success
                  </th>
                  <th scope="col" className="wf-table__th wf-table__th--num">
                    Errors
                  </th>
                  <th scope="col" className="wf-table__th wf-col--date">
                    Created
                  </th>
                  <th scope="col" className="wf-table__th wf-col--owner">
                    Owner
                  </th>
                  <th scope="col" className="wf-table__th wf-table__th--actions">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((wf) => {
                  const running = isRunningStatus(wf.status);
                  const runs = wf.successful_runs ?? 0;
                  const errs = wf.errors_count ?? 0;
                  const statusLabel = (wf.status ?? 'Unknown').trim();
                  const createdLabel = formatShortDate(wf.created_on);
                  const desc = wf.description?.trim() ? wf.description : '—';
                  const owner = (wf.created_by ?? '').trim() || '—';
                  return (
                    <tr
                      key={wf.id}
                      className={`wf-table__tr${running ? ' wf-table__tr--running' : ''}`}
                    >
                      <td className="wf-table__td">
                        <div className="wf-cell-workflow">
                          <div className="wf-cell-workflow__icon" aria-hidden>
                            <WorkflowRowIcon iconRef={wf.icon} />
                          </div>
                          <div className="wf-cell-workflow__text">
                            <span className="wf-cell-workflow__name" title={wf.name}>
                              {wf.name}
                            </span>
                            <span className="wf-cell-workflow__id" title={wf.id}>
                              Post workflow · {wf.id.length > 10 ? `${wf.id.slice(0, 8)}…` : wf.id}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="wf-table__td wf-col--desc">
                        <span className="wf-cell-desc" title={desc === '—' ? undefined : desc}>
                          {desc}
                        </span>
                      </td>
                      <td className="wf-table__td">
                        <span
                          className={`wf-pill-status ${running ? 'wf-pill-status--active' : 'wf-pill-status--idle'}`}
                          title={statusLabel}
                        >
                          {running ? 'Running' : statusLabel}
                        </span>
                      </td>
                      <td
                        className={`wf-table__td wf-table__td--num${runs > 0 ? ' wf-table__td--num-pos' : ''}`}
                        title="Successful runs"
                      >
                        {runs}
                      </td>
                      <td className="wf-table__td wf-table__td--num wf-table__td--errs" title="Errors">
                        {errs}
                      </td>
                      <td className="wf-table__td wf-col--date">
                        <span className="wf-cell-date">{createdLabel}</span>
                      </td>
                      <td className="wf-table__td wf-col--owner">
                        <span className="wf-cell-owner" title={owner !== '—' ? owner : undefined}>
                          {owner}
                        </span>
                      </td>
                      <td className="wf-table__td wf-table__td--actions">
                        <div className="wf-row-actions">
                          <button
                            type="button"
                            className="wf-row-actions__primary"
                            onClick={() => goToBuilder(wf.id)}
                            title="Open in builder"
                          >
                            <Eye className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                            Open
                          </button>
                          <button
                            type="button"
                            className="wf-icon-btn"
                            onClick={() => openScheduleModal(wf)}
                            title="Schedule"
                            aria-label={`Schedule ${wf.name}`}
                          >
                            <UserCog strokeWidth={1.75} size={17} aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="wf-icon-btn"
                            onClick={() => goToBuilder(wf.id)}
                            title="Edit in builder"
                            aria-label={`Edit ${wf.name} in builder`}
                          >
                            <Pencil strokeWidth={1.75} size={17} aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="wf-icon-btn"
                            onClick={() => openLogs(wf)}
                            title="Logs"
                            aria-label={`Logs for ${wf.name}`}
                          >
                            <ListChecks strokeWidth={1.75} size={17} aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

        </div>
      </div>

      {showScheduleModal && selectedWorkflow && (
        <EditScheduleModal
          prefill={selectedWorkflow}
          onClose={() => setShowScheduleModal(false)}
          onSubmit={(updated) => {
            console.log('Schedule updated:', updated);
          }}
        />
      )}

      {showLogsModal && activeLogs && (
        <LogsModal
          workflowId={activeLogs.workflowId}
          name={activeLogs.name}
          onClose={() => setShowLogsModal(false)}
        />
      )}

      {showProjectsModal ? (
        <WorkflowProjectsModal
          onClose={() => {
            setShowProjectsModal(false);
            void loadProjects();
          }}
        />
      ) : null}
    </div>
  );
};

export default Workflows;
